/**
 * START A PARTY — the three-step wizard, shared by both trees.
 * `design/GT Crew.dc.html` and `design/GT Mobile Crew.dc.html`
 * (`data-screen-label="Start a party"`). The mirrors still say CREW: the
 * rename landed in the app on 2026-08-28 and the prototype owes it upstream.
 *
 * ONE COMPONENT, TWO METRIC SETS. The two mirrors draw the same card with the
 * same controls in the same order; what differs is measurements — 560px vs
 * full-width, the paddings, and a handful of font sizes (the desktop rail is
 * 9px/9px where the phone is 7px/8px, labels 9px vs 8.5px). Those differences
 * are real and worth keeping, but they are not worth 350 duplicated lines that
 * would then drift apart, so they live in `WizardMetrics` and each tree's
 * screen file passes its own.
 *
 * Bare chrome, like `landing` and `login`: it is reachable signed out, and a
 * header naming a party you do not have yet would be nonsense.
 *
 * The rail is the back control (DESIGN-CHANGELOG 2026-08-18): a step you have
 * passed is clickable and keeps everything you typed. Steps 1 and 2 lock on
 * step 3 with the reason on hover, because FORM THE PARTY commits the account
 * and the party together — by then there is no in-progress form to return to.
 *
 * DEVIATION, flagged: the invite email on step 3 is REQUIRED here, where the
 * mirror still labels it `EMAIL — OPTIONAL`. Emails became required on
 * 2026-08-18 (Q7) and `admin-users op=create` refuses without one, so keeping
 * the stale label would only move the refusal to the server. The changelog
 * entry for that copy is owed upstream (X-HANDOFF §2.6).
 */

import type { CSSProperties } from 'react';

import { DISPLAY_NAME_MAX } from '../lib/crewSlug.ts';
import { useQd } from '../store/index.ts';
import { PixelButton } from './PixelButton.tsx';
import { QuarterIcon } from './QuarterIcon.tsx';

/** The measurements that differ between the two mirrors. */
export interface WizardMetrics {
  /** Outer page padding. */
  pad: string;
  /** Card width — the phone column is fluid, the desktop card is 560px. */
  width: string;
  /** Card padding. */
  cardPad: string;
  /** Gap between the card's rows. */
  gap: string;
  /** Field label size. */
  labelFs: string;
  /** Rail number / rail label sizes. */
  railNumFs: string;
  railLabelFs: string;
}

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '12px',
  padding: '13px 12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const LABEL: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  letterSpacing: '.08em',
  color: 'var(--gt-muted)',
};

const ERR: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '10.5px',
  color: 'var(--acc2)',
};

const FIELD_GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '5px' };

export function StartCrewCard({ m }: { m: WizardMetrics }): JSX.Element {
  const s = useQd();
  const rail = s.wzRail();

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: m.pad,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: m.width,
          maxWidth: '100%',
          background: 'var(--gt-card)',
          border: '1px solid var(--gt-border)',
          boxShadow: 'var(--gt-shadow-panel)',
          padding: m.cardPad,
          display: 'flex',
          flexDirection: 'column',
          gap: m.gap,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <QuarterIcon u={1} hover={false} />
          <span
            style={{
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: '8px',
              color: 'var(--gt-text)',
              transform: 'translateY(0.13em)',
            }}
          >
            START A PARTY
          </span>
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '9px',
              letterSpacing: '.06em',
              color: 'var(--gt-muted-2)',
            }}
          >
            STEP {s.wzStep}/3
          </span>
        </div>

        {/* The rail — also the back control. */}
        <div style={{ display: 'flex', paddingLeft: '1px' }}>
          {rail.map((step, i) => (
            <div
              key={step.num}
              onClick={() => s.wzGoStep((i + 1) as 1 | 2 | 3)}
              title={step.tip}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px 4px',
                border: '1px solid ' + (step.on ? 'var(--acc)' : 'var(--gt-border-2)'),
                background: step.on ? 'var(--gt-raised)' : 'transparent',
                color: step.on ? 'var(--acc)' : step.can ? 'var(--gt-text-3)' : 'var(--gt-ghost-2)',
                marginLeft: i === 0 ? 0 : '-1px',
                position: 'relative',
                zIndex: step.on ? 2 : 1,
                cursor: step.can ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: m.railNumFs,
                  transform: 'translateY(0.13em)',
                }}
              >
                {step.num}
              </span>
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: m.railLabelFs,
                  letterSpacing: '.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>

        {s.wzStep === 1 ? (
          <>
            <div style={FIELD_GROUP}>
              <span style={{ ...LABEL, fontSize: m.labelFs }}>DISPLAY NAME</span>
              <input
                value={s.wzName}
                onChange={(e) => s.setWz({ wzName: e.target.value })}
                maxLength={DISPLAY_NAME_MAX}
                placeholder="MARCUS"
                style={FIELD}
              />
            </div>
            <div style={FIELD_GROUP}>
              <span style={{ ...LABEL, fontSize: m.labelFs }}>EMAIL</span>
              <input
                value={s.wzEmail}
                onChange={(e) => s.setWz({ wzEmail: e.target.value })}
                type="email"
                placeholder="MARCUS@EXAMPLE.COM"
                style={FIELD}
              />
            </div>
            <div style={FIELD_GROUP}>
              <span style={{ ...LABEL, fontSize: m.labelFs }}>PASSWORD — 8+ CHARACTERS</span>
              <input
                type="password"
                value={s.wzP1}
                onChange={(e) => s.setWz({ wzP1: e.target.value })}
                placeholder="••••••••"
                style={FIELD}
              />
              <input
                type="password"
                value={s.wzP2}
                onChange={(e) => s.setWz({ wzP2: e.target.value })}
                placeholder="CONFIRM PASSWORD"
                style={FIELD}
              />
            </div>
            {s.wzErr ? <div style={ERR}>✕ {s.wzErr}</div> : null}
            <PixelButton
              label="NEXT — NAME THE PARTY ▸"
              onClick={s.wzNext1}
              fs="8.5"
              pad="15px 14px"
              center
            />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span
                onClick={() => s.nav('landing')}
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '9px',
                  letterSpacing: '.06em',
                  color: 'var(--gt-ghost-2)',
                  cursor: 'pointer',
                  padding: '8px',
                }}
              >
                ‹ BACK TO QUARTERDOWN.COM
              </span>
            </div>
          </>
        ) : null}

        {s.wzStep === 2 ? (
          <>
            <div style={FIELD_GROUP}>
              <span style={{ ...LABEL, fontSize: m.labelFs }}>PARTY NAME</span>
              <input
                value={s.wzCrew}
                onChange={(e) => s.setWz({ wzCrew: e.target.value })}
                maxLength={24}
                placeholder="PIZZA DOGS"
                style={FIELD}
              />
              {/* THIS USED TO PROMISE THE ADDRESS AS YOU TYPED —
                  `quarterdown.com/c/pizza-dogs`, live off the crew name. Since
                  3031 the address is opaque and name-independent
                  (`crew-x7k4qd`), so that preview would have been a promise the
                  wizard could not keep.

                  The address stopped being derived from the name so that
                  RENAME does not leave it saying the old word — a crew's
                  address is in invitation links already sent and in bookmarks,
                  so it can never follow a rename. An address that never claimed
                  to match has nothing to go stale. Marcus's call, 2026-08-21. */}
              <div
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '9.5px',
                  color: 'var(--gt-muted-2)',
                  overflowWrap: 'anywhere',
                }}
              >
                URL <span style={{ color: 'var(--acc)' }}>ASSIGNED WHEN YOU START THE PARTY</span>
              </div>
            </div>
            {s.wzErr ? <div style={ERR}>✕ {s.wzErr}</div> : null}
            {/* The slowest action in the product: an account, a crew, a
                membership and a full hydrate. `wzCreate` has always dropped a
                second press (`if (s.wzBusy) return`), but nothing said so, so
                the press read as ignored and people pressed again. */}
            <PixelButton
              label={s.wzBusy ? 'FORMING YOUR PARTY…' : 'FORM THE PARTY ▸'}
              busy={s.wzBusy}
              onClick={() => void s.wzCreate()}
              fs="8.5"
              pad="15px 14px"
              center
            />
            {/* Only the signed-out flow has an account form to go back to. */}
            {s.wzEntryStep === 1 ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <span
                  onClick={() => s.wzGoStep(1)}
                  style={{
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: '9px',
                    color: 'var(--gt-muted)',
                    cursor: 'pointer',
                    padding: '8px',
                  }}
                >
                  ‹ BACK — YOUR ACCOUNT
                </span>
              </div>
            ) : null}
          </>
        ) : null}

        {s.wzStep === 3 ? (
          <>
            <div
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9.5px',
                lineHeight: 1.8,
                color: 'var(--acc)',
              }}
            >
              INVITE PLAYERS
            </div>
            {/* ONE FIELD, the same as MANAGE PARTY's. The PLAYER NAME box is
                gone: they pick their own on the way in.

                ONE ROW: the address takes the space and INVITE takes only what
                it needs. Full-width the button read as the step's primary
                action, competing with START directly beneath it — two big
                identical bars, one of which finishes the wizard. `flex: 'none'`
                on the host wrapper is the supported way to size a PixelButton
                from outside; its own `style` prop is positional-only. */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
              <input
                value={s.wzIEmail}
                onChange={(e) => s.setWz({ wzIEmail: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.repeat) void s.wzAddInvite();
                }}
                type="email"
                placeholder="EMAIL ADDRESS"
                // `minWidth: 0` or the input refuses to shrink below its
                // intrinsic width and pushes the button off the phone column.
                style={{ ...FIELD, fontSize: '11.5px', flex: 1, minWidth: 0 }}
              />
              <PixelButton
                label={s.wzBusy ? 'INVITING…' : 'INVITE'}
                busy={s.wzBusy}
                onClick={() => void s.wzAddInvite()}
                fs="8"
                pad="14px 18px"
                center
                nowrap
                // `display:flex` on the host as well as `flex:none`: the host
                // stretches to the row, and this makes the button inside it
                // stretch to the host. Without it the two sit at whatever
                // heights their own padding and font metrics produce, which is
                // close but visibly not equal.
                style={{ flex: 'none', display: 'flex' }}
              />
            </div>
            {s.wzIErr ? <div style={ERR}>✕ {s.wzIErr}</div> : null}

            {/* WHAT THIS LIST IS NOW: addresses that have been invited, and
                nothing else. It used to show a name, a temp password and a link
                per row, because each INVITE minted a real account on the spot.
                There is no account and no password to show until each person
                accepts — and no link either, because the invitation was
                emailed to them (3026, docs/INVITES.md). */}
            {s.wzInvites.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {s.wzInvites.map((email) => (
                  <div
                    key={email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      border: '1px dashed var(--gt-border-2)',
                      padding: '10px 12px',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--gt-font-pixel)',
                        fontSize: '7px',
                        color: 'var(--acc)',
                        flex: 'none',
                      }}
                    >
                      INVITED
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--gt-font-mono)',
                        fontSize: '10.5px',
                        color: 'var(--gt-text-2)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {email}
                    </span>
                  </div>
                ))}
                <span
                  style={{
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: '9px',
                    lineHeight: 1.8,
                    letterSpacing: '.05em',
                    color: 'var(--gt-muted-2)',
                  }}
                >
                  THEY PICK THEIR OWN NAME AND PASSWORD WHEN THEY JOIN.
                </span>
              </div>
            ) : null}

            {/* START must wait for an invitation that is still being sent:
                `wzFinish` blanks the wizard, and `blank` resets `wzBusy`, so
                finishing mid-invite drops the guard and throws away the result
                when it lands. */}
            <PixelButton
              label={s.wzBusy ? 'FINISHING…' : 'START'}
              busy={s.wzBusy}
              onClick={s.wzFinish}
              fs="8.5"
              pad="15px 14px"
              center
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
